import Phaser from 'phaser';
import { animationConfig, animationEase } from '@config/animation.config';
import type { BanknoteViewResult } from '@ui/components/BanknoteView';
import { delayPromise, killViewTweens, tweenPromise } from './tweenUtils';

/**
 * Banknot ve oyun akışı animasyonları.
 */
export class GameplayAnimations {
  static async fadeHint(
    scene: Phaser.Scene,
    hintText: Phaser.GameObjects.Text,
    nextText: string,
  ): Promise<void> {
    await tweenPromise(scene, {
      targets: hintText,
      alpha: 0,
      duration: animationConfig.hintFadeMs,
      ease: animationEase.inCubic,
    });

    hintText.setText(nextText);

    await tweenPromise(scene, {
      targets: hintText,
      alpha: 1,
      duration: animationConfig.hintFadeMs,
      ease: animationEase.outCubic,
    });
  }

  static async previewEnter(scene: Phaser.Scene, view: BanknoteViewResult): Promise<void> {
    const { container } = view;
    container.setAlpha(0);
    container.setScale(0.72);

    await tweenPromise(scene, {
      targets: container,
      alpha: 1,
      scale: 1,
      duration: animationConfig.previewEnterMs,
      ease: animationEase.outBack,
    });

    scene.tweens.add({
      targets: container,
      scale: animationConfig.previewPulseScale,
      duration: animationConfig.previewPulseMs,
      yoyo: true,
      repeat: -1,
      ease: animationEase.outQuad,
    });
  }

  static async previewExit(scene: Phaser.Scene, view: BanknoteViewResult): Promise<void> {
    killViewTweens(scene, view.container);

    await tweenPromise(scene, {
      targets: view.container,
      alpha: 0,
      scale: 0.88,
      duration: animationConfig.previewExitMs,
      ease: animationEase.inCubic,
    });
  }

  static async gridEnter(scene: Phaser.Scene, views: BanknoteViewResult[]): Promise<void> {
    const tasks = views.map((view, index) =>
      delayPromise(scene, index * animationConfig.gridStaggerDelayMs).then(() => {
        view.container.setAlpha(0);
        view.container.setScale(0.55);
        view.hitArea.setAlpha(0);

        return tweenPromise(scene, {
          targets: view.container,
          alpha: 1,
          scale: 1,
          duration: animationConfig.gridEnterMs,
          ease: animationEase.outBack,
        }).then(() => {
          view.hitArea.setAlpha(1);
        });
      }),
    );

    await Promise.all(tasks);
  }

  static playCorrect(
    scene: Phaser.Scene,
    selected: BanknoteViewResult,
    others: BanknoteViewResult[],
    scoreText: Phaser.GameObjects.Text,
  ): Phaser.GameObjects.Graphics {
    others.forEach((view) => {
      scene.tweens.add({
        targets: [view.container, view.hitArea],
        alpha: animationConfig.correctDimAlpha,
        duration: animationConfig.correctOverlayMs,
        ease: animationEase.outCubic,
      });
    });

    selected.hitArea.disableInteractive();

    scene.tweens.add({
      targets: selected.container,
      scale: animationConfig.correctBounceScale,
      duration: animationConfig.correctBounceMs,
      yoyo: true,
      ease: animationEase.outBack,
    });

    scene.tweens.add({
      targets: scoreText,
      scale: animationConfig.scorePunchScale,
      duration: animationConfig.scorePunchMs,
      yoyo: true,
      ease: animationEase.outBack,
    });

    return this.addFlashOverlay(scene, selected, 0x3ddc84);
  }

  static playWrong(
    scene: Phaser.Scene,
    selected: BanknoteViewResult,
    correct: BanknoteViewResult,
    others: BanknoteViewResult[],
  ): void {
    scene.cameras.main.shake(
      animationConfig.wrongShakeDurationMs,
      animationConfig.wrongShakeIntensity,
    );

    const dimTargets = others
      .filter((view) => view.banknoteId !== correct.banknoteId)
      .flatMap((view) => [view.container, view.hitArea]);

    scene.tweens.add({
      targets: dimTargets,
      alpha: animationConfig.correctDimAlpha,
      duration: animationConfig.wrongOverlayMs,
      ease: animationEase.outCubic,
    });

    selected.hitArea.disableInteractive();
    others.forEach((view) => view.hitArea.disableInteractive());

    scene.tweens.add({
      targets: selected.container,
      x: selected.container.x - 10,
      duration: 50,
      yoyo: true,
      repeat: 3,
      ease: animationEase.outQuad,
    });

    this.addFlashOverlay(scene, selected, 0xff5c5c);

    scene.tweens.add({
      targets: correct.container,
      scale: 1.08,
      duration: animationConfig.wrongOverlayMs,
      yoyo: true,
      repeat: 2,
      ease: animationEase.outBack,
    });

    this.addFlashOverlay(scene, correct, 0x3ddc84);
  }

  private static addFlashOverlay(
    scene: Phaser.Scene,
    view: BanknoteViewResult,
    color: number,
  ): Phaser.GameObjects.Graphics {
    const cornerRadius = Math.round(view.height * 0.09);
    const overlay = scene.add.graphics();
    overlay.fillStyle(color, 0.45);
    overlay.fillRoundedRect(
      view.container.x - view.width / 2,
      view.container.y - view.height / 2,
      view.width,
      view.height,
      cornerRadius,
    );
    overlay.setAlpha(0);

    scene.tweens.add({
      targets: overlay,
      alpha: 1,
      duration: animationConfig.correctOverlayMs,
      yoyo: true,
      ease: animationEase.outQuad,
    });

    return overlay;
  }
}
